import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { LogoMark } from "./Logo";

const DemoModalContext = createContext({ openDemo: () => {} });
export const useDemoModal = () => useContext(DemoModalContext);

const CAPTURE_SRC = "https://admin.kryvocrm.com/kryvo-capture.js";
const CAPTURE_TOKEN = "f38d66efe51c9b4453b10a0c5e643a72f5a5";
const CAPTURE_ENDPOINT = "https://yzbdrrqfcjqjsciujsig.supabase.co/functions/v1/lead-capture-ingest";
const MOUNT_ID = "kryvo-form-f38d66";

const KryvoCaptureEmbed = ({ onSubmitted }) => {
    const containerRef = useRef(null);

    useEffect(() => {
        window.kryvoOnSubmit = () => onSubmitted();
        const script = document.createElement("script");
        script.src = CAPTURE_SRC;
        script.setAttribute("data-kryvo-token", CAPTURE_TOKEN);
        script.setAttribute("data-kryvo-endpoint", CAPTURE_ENDPOINT);
        script.async = true;
        containerRef.current?.appendChild(script);
        return () => {
            delete window.kryvoOnSubmit;
            script.remove();
            const host = document.getElementById(MOUNT_ID);
            if (host) host.innerHTML = "";
        };
    }, [onSubmitted]);

    return (
        <div ref={containerRef} data-testid="demo-capture-embed" className="mt-2">
            <div id={MOUNT_ID} />
        </div>
    );
};

const BookDemoModal = ({ open, onOpenChange }) => {
    const [done, setDone] = useState(false);

    const close = (v) => {
        onOpenChange(v);
        if (!v) setTimeout(() => setDone(false), 300);
    };

    const handleSubmitted = () => {
        setDone(true);
        toast.success("Demo request received. Our team will reach out shortly.");
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent
                data-testid="book-demo-modal"
                className="no-scrollbar max-h-[92vh] overflow-y-auto rounded-2xl border-[#E5E7EB] p-0 sm:max-w-xl"
            >
                <AnimatePresence mode="wait">
                    {done ? (
                        <motion.div
                            key="success"
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="flex flex-col items-center px-8 py-14 text-center"
                        >
                            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F0FDF4]">
                                <CheckCircle2 className="h-8 w-8 text-[#16A34A]" />
                            </span>
                            <h3 className="mt-6 font-display text-2xl font-bold text-[#111827]">
                                Request received.
                            </h3>
                            <p className="mt-3 max-w-sm text-[15px] leading-relaxed text-[#64748B]">
                                Thanks for your interest in Kryvo CRM. Your enquiry has
                                landed directly with our team — a product specialist will
                                contact you within one business day to schedule your demo.
                            </p>
                            <button
                                data-testid="demo-success-close-button"
                                onClick={() => close(false)}
                                className="mt-8 rounded-full bg-[#16A34A] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#15803D]"
                            >
                                Done
                            </button>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="form"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="px-5 py-6 sm:px-7"
                        >
                            <DialogHeader>
                                <div className="flex items-center gap-3">
                                    <LogoMark size={36} />
                                    <div>
                                        <DialogTitle className="font-display text-xl font-bold text-[#111827]">
                                            Book a personalized demo
                                        </DialogTitle>
                                        <DialogDescription className="mt-1 text-sm text-[#64748B]">
                                            See Kryvo CRM with your own sales process. 30 minutes, no commitment.
                                        </DialogDescription>
                                    </div>
                                </div>
                            </DialogHeader>
                            {open && <KryvoCaptureEmbed onSubmitted={handleSubmitted} />}
                        </motion.div>
                    )}
                </AnimatePresence>
            </DialogContent>
        </Dialog>
    );
};

export const DemoModalProvider = ({ children }) => {
    const [open, setOpen] = useState(false);
    return (
        <DemoModalContext.Provider value={{ openDemo: () => setOpen(true) }}>
            {children}
            <BookDemoModal open={open} onOpenChange={setOpen} />
        </DemoModalContext.Provider>
    );
};
